<!-- 登录页 -->
<template>
  <div
    class="login-form"
    :style="
      isDialog
        ? 'padding: 0px 15px 10px 15px;'
        : 'padding: 40px 30px;box-shadow: 0px 0px 10px 0px rgba(206, 206, 206, 0.4)'
    "
  >
    <div class="form-title">
      <span>欢迎使用{{ clientName }}</span>
    </div>
    <div class="form-content">
      <el-tabs
        v-model="activeTabName"
        class="form-tabs"
        @tab-click="handleClickTab"
      >
        <el-tab-pane
          label="密码登录"
          name="account-login"
          :disabled="
            (buttonLoading && activeTabName !== 'account-login') ||
            sendCodeDisable
          "
        >
          <el-form
            :model="loginForm"
            :rules="formRules"
            ref="accountLoginFormRef"
          >
            <el-form-item prop="loginName">
              <el-input
                v-model="loginForm.loginName"
                placeholder="请输入手机号/邮箱/用户名"
                type="text"
                @input="handleValidateLoginName"
              >
                <template #prefix>
                  <i class="iconfont icon-dengluyonghu" />
                </template>
              </el-input>
            </el-form-item>
            <el-form-item prop="password">
              <el-input
                v-model="loginForm.password"
                placeholder="请输入密码"
                type="password"
                show-password
              >
                <template #prefix>
                  <i class="iconfont icon-denglumima" />
                </template>
                <template #suffix>
                  <span class="input-suffix-link" @click="getPassword">
                    忘记密码
                  </span>
                </template>
              </el-input>
            </el-form-item>
            <el-form-item class="login-buttons">
              <el-button
                class="register-btn"
                @click="activeTabName = 'code-login'"
              >
                注册
              </el-button>
              <el-button
                :loading="buttonLoading"
                type="primary"
                @click="accountLogin()"
              >
                登录
              </el-button>
            </el-form-item>
          </el-form>
        </el-tab-pane>
        <el-tab-pane
          label="短信登录"
          name="code-login"
          :disabled="buttonLoading && activeTabName !== 'code-login'"
        >
          <el-form :model="loginForm" :rules="formRules" ref="codeLoginFormRef">
            <el-form-item prop="loginName">
              <el-input
                v-model="loginForm.loginName"
                placeholder="请输入手机号"
                type="text"
                @input="handleValidateLoginName"
              >
                <template #prefix>
                  <i class="iconfont icon-dengluyonghu" />
                </template>
              </el-input>
            </el-form-item>
            <el-form-item prop="code">
              <el-input
                v-model="loginForm.code"
                placeholder="请输入6位数字验证码"
                :maxlength="6"
              >
                <template #prefix>
                  <i class="iconfont icon-yanzhengma" />
                </template>
                <template #suffix>
                  <span
                    class="input-suffix-link"
                    :class="{ 'is-disabled': !ifCanSendCode }"
                    @click="ifCanSendCode ? showGraphicCaptcha() : ''"
                  >
                    {{ sendCodeText }}
                  </span>
                </template>
              </el-input>
            </el-form-item>
            <el-form-item>
              <el-button
                :loading="buttonLoading"
                type="primary"
                @click="codeLogin()"
              >
                登录/注册
              </el-button>
            </el-form-item>
          </el-form>
        </el-tab-pane>
      </el-tabs>
    </div>
    <div class="agreement-tip">
      <p>未注册过的手机号，我们将自动帮你注册账号</p>
      <p>
        登录或完成注册即代表你同意
        <a
          href="https://www.ringdata.com/user_agreement"
          target="_blank"
          rel="noopener noreferrer"
        >
          用户协议
        </a>
        和
        <a
          href="https://www.ringdata.com/privacy_policy"
          target="_blank"
          rel="noopener noreferrer"
        >
          隐私政策
        </a>
      </p>
    </div>
  </div>
</template>
<script setup>
import crypto from "crypto";
import store from "@/store";
import {
  ref,
  onMounted,
  computed,
  defineProps,
  defineEmits,
  defineExpose,
  nextTick,
} from "vue";
import { checkPhone } from "@/utils/validation";
import { useRouter, useRoute } from "vue-router";
import { httpPostToken, httpPostIsAnonymous } from "@/api/httpService";
import { ElMessage } from "element-plus";
import { goFindPassword } from "@/utils/login.js";
import { remindErrorMessage } from "@/utils/utils";
import { getAliCaptchaInstance } from "@/utils/aliCaptcha";

const router = useRouter();
const route = useRoute();
//路由参数：登录后目标路由；客户端类型、名称
const targetUrl = ref("");
const clientType = ref("");
const clientName = ref("科研教学平台");

const props = defineProps({
  isDialog: {
    type: Boolean,
    default: false,
  }, //是否为登录弹框
});
const emits = defineEmits(["login"]);

const activeTabName = ref("account-login");

/** 与 targetUrl 组合，避免不同产品线回跳互相串 Tab */
const loginTabPrefStorageKey = () => {
  const ct = route.params.clientType ?? "";
  const tu = route.params.targetUrl ?? "";
  return `uap_prefer_code_login:${ct}:${encodeURIComponent(String(tu))}`;
};

const applySmsLoginTab = () => {
  activeTabName.value = "code-login";
  loginForm.value.loginName = "";
  loginForm.value.code = "";
};

//#region 登录表单
const loginForm = ref({
  loginName: "",
  password: "",
  code: "",
});

const handleValidateLoginName = () => {
  if (activeTabName.value === "account-login") {
    accountLoginFormRef.value?.validateField("loginName");
  } else {
    codeLoginFormRef.value?.validateField("loginName");
  }
};
const validateLoginName = (rule, value, callback) => {
  value = value.trim();
  if (activeTabName.value === "account-login") {
    if (value === "") {
      callback(new Error("请输入手机号/邮箱/用户名"));
    } else {
      callback();
    }
  } else {
    if (value === "") {
      callback(new Error("请输入手机号"));
    } else if (!checkPhone(value)) {
      callback(new Error("请输入正确的手机号"));
    } else {
      callback();
    }
  }
};
const formRules = ref({
  loginName: [
    {
      required: true,
      validator: validateLoginName,
      trigger: [],
    },
  ],
  password: [
    {
      required: true,
      message: "请输入密码",
      trigger: "blur",
    },
  ],
  code: [
    {
      required: true,
      message: "请输入验证码",
      trigger: "blur",
    },
  ],
});
//#endregion 登录表单

//#region 验证码相关
const sendCodeDisable = ref(false);
const sendCodeText = ref("获取验证码");
const countdown = ref(60); //倒计时
const aliCaptchaRef = ref(null);
const aliCaptchaLoadStatus = ref("loading");

const ifCanSendCode = computed(() => {
  return (
    loginForm.value.loginName.trim() !== "" &&
    !sendCodeDisable.value &&
    checkPhone(loginForm.value.loginName.trim())
  );
});

const showGraphicCaptcha = () => {
  if (aliCaptchaRef.value) {
    aliCaptchaRef.value.showCaptcha((validateData) => {
      sendCode(validateData);
    });
    return;
  }
  if (aliCaptchaLoadStatus.value === "error") {
    ElMessage.warning("图形验证码未加载成功，请刷新页面后重试");
    return;
  }
  ElMessage.info("图形验证码正在加载，请稍后再试");
};

const sendCode = async (validateData) => {
  settime();
  const params = {
    account: loginForm.value.loginName.trim(),
    ...(validateData && {
      lotNumber: validateData.lotNumber,
      captchaOutput: validateData.captchaOutput,
      passToken: validateData.passToken,
      genTime: validateData.genTime,
    }),
  };
  const res = await httpPostIsAnonymous(
    "/identity/v1/sendPhoneLoginCode",
    params
  );
  if (res.code === 0) {
    ElMessage.success("发送成功，请注意查收");
  } else {
    remindErrorMessage(res.message);
    countdown.value = 0;
    if (aliCaptchaRef.value) aliCaptchaRef.value.reset();
  }
};

const settime = () => {
  if (countdown.value <= 0) {
    sendCodeDisable.value = false;
    sendCodeText.value = "重新发送";
    countdown.value = 60;
    if (aliCaptchaRef.value) aliCaptchaRef.value.reset();
  } else {
    sendCodeDisable.value = true;
    sendCodeText.value = countdown.value + "s";
    countdown.value--;
    setTimeout(() => {
      settime();
    }, 1000);
  }
};
//#endregion 验证码相关

//登录按钮
const buttonLoading = ref(false);
const showButtonLoading = () => {
  buttonLoading.value = true;
};
const hideButtonLoading = () => {
  buttonLoading.value = false;
};
//帐号登录
const accountLoginFormRef = ref(null);
const codeLoginFormRef = ref(null);
const accountLogin = () => {
  accountLoginFormRef.value.validate(async (valid) => {
    if (valid) {
      showButtonLoading();
      let ip = window.localStorage.getItem("ip");
      let city = window.localStorage.getItem("city");
      let client = clientType.value;
      //MD5加密
      let md5Password;
      if (loginForm.value.password.toString().length != 32) {
        const md5 = crypto.createHash("md5");
        md5.update(loginForm.value.password);
        md5Password = md5.digest("hex");
      } else {
        md5Password = loginForm.value.password;
      }
      let res = await httpPostToken(
        "/oauth/token",
        `grant_type=password&username=${loginForm.value.loginName.trim()}&password=${md5Password}&clientType=${client}&ip=${ip}&city=${city}`
      );
      if (res.code === 0) {
        if (!props.isDialog) {
          window.location.href = decodeURI(targetUrl.value);
        } else {
          emits("login");
        }
        store.commit("setLoginStatus", {
          status: true,
        });
        ElMessage.success("登录成功");
      }
      hideButtonLoading();
    } else {
      return false;
    }
  });
};
//验证码登录
const codeLogin = () => {
  codeLoginFormRef.value.validate(async (valid) => {
    if (valid) {
      showButtonLoading();
      let ip = window.localStorage.getItem("ip");
      let city = window.localStorage.getItem("city");
      let client = clientType.value;
      let res = await httpPostToken(
        "/oauth/token",
        `grant_type=password&username=${loginForm.value.loginName.trim()}&sms_login_code=${
          loginForm.value.code
        }&clientType=${client}&ip=${ip}&city=${city}`
      );
      if (res.code === 0) {
        if (!props.isDialog) {
          window.location.href = decodeURI(targetUrl.value);
        } else {
          emits("login");
        }
        store.commit("setLoginStatus", {
          status: true,
        });
        ElMessage.success("登录成功");
      }
      hideButtonLoading();
    } else {
      return false;
    }
  });
};

const getPassword = () => {
  goFindPassword();
};

//页面初始化方法
const init = () => {
  clientType.value = route.params.clientType || "CDN";
  targetUrl.value = route.params.targetUrl;
  clientName.value = "科研教学平台";
  try {
    window.localStorage.removeItem("login_name");
    window.localStorage.removeItem("login_password");
  } catch (_) {
    /* ignore */
  }
  formInit();
  const prefKey = loginTabPrefStorageKey();
  if (route.query?.loginTab === "code-login") {
    window.sessionStorage.setItem(prefKey, "code-login");
    applySmsLoginTab();
  } else if (window.sessionStorage.getItem(prefKey) === "code-login") {
    applySmsLoginTab();
  }
};

const formInit = () => {
  loginForm.value.loginName = "";
  loginForm.value.password = "";
  loginForm.value.code = "";
};

const handleClickTab = (tab) => {
  if (tab.paneName === "account-login") {
    window.sessionStorage.removeItem(loginTabPrefStorageKey());
    formInit();
  } else {
    loginForm.value.loginName = "";
    loginForm.value.code = "";
  }
};

onMounted(() => {
  init();
  if (route.query?.loginTab === "code-login") {
    nextTick(() => {
      const q = { ...route.query };
      delete q.loginTab;
      router.replace({ path: route.path, query: q, hash: route.hash });
    });
  }
  getAliCaptchaInstance()
    .then((instance) => {
      aliCaptchaRef.value = instance;
      aliCaptchaLoadStatus.value = "ready";
    })
    .catch((err) => {
      console.warn("阿里云图形验证码加载失败", err);
      aliCaptchaLoadStatus.value = "error";
    });
});

defineExpose({ init });
</script>
<style lang="scss" scoped>
:deep(.el-tabs__content) {
  padding: 3px;
}
.login-form {
  min-height: 370px;
  border-radius: 10px;
  .form-title {
    text-align: left;
    margin-bottom: 30px;
    span {
      color: #303133;
      font-size: 24px;
      font-weight: 550;
      font-family: SourceHanSansSC-regular;
    }
  }
  .form-content {
    .input-suffix-link {
      font-size: 13px;
      white-space: nowrap;
      color: var(--primary-color, #1963d4);
      cursor: pointer;
      &.is-disabled {
        cursor: not-allowed;
        color: #a8abb2;
      }
    }
    :deep(.el-input__suffix-inner) {
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }
    .login-buttons {
      :deep(.el-form-item__content) {
        display: flex;
        gap: 20px;
      }
      :deep(.el-button) {
        flex: 1;
        width: auto;
        min-width: 0;
      }
    }
  }
  .agreement-tip {
    margin-top: 20px;
    text-align: center;
    font-size: 13px;
    color: #999999;
    line-height: 1.6;
    p {
      margin: 0 0 8px;
    }
    a {
      color: $ringdata-color;
      text-decoration: none;
    }
    a:hover {
      text-decoration: underline;
    }
  }
  :deep(.el-tabs__header) {
    margin-bottom: 30px;
    .el-tabs__item {
      font-weight: 550;
      font-size: 16px;
    }
  }
  :deep(.el-button--primary) {
    background-color: $ringdata-color;
    border-color: $ringdata-color;
    color: #ffffff;
  }
  :deep(.el-button) {
    border-radius: 8px;
    height: 40px;
    font-size: 14px;
    font-family: Roboto;
  }
  :deep(.el-form-item:not(.login-buttons) .el-button) {
    width: 100%;
  }
  :deep(.el-button + .el-button) {
    margin-left: 0px;
  }
  .form-content .login-buttons :deep(.register-btn.el-button) {
    background-color: #ffffff !important;
    border-color: #dcdcdc !important;
    color: #303133 !important;
  }
  .form-content .login-buttons :deep(.register-btn.el-button:hover),
  .form-content .login-buttons :deep(.register-btn.el-button:focus) {
    background-color: #f5f7fa !important;
    border-color: #c0c4cc !important;
    color: #303133 !important;
  }
  :deep(.el-input__wrapper) {
    border-color: #dcdcdc;
    height: 40px;
    line-height: 40px;
    border-radius: 8px;
    font-family: Roboto;
  }
  :deep(.el-input__wrapper.is-focus) {
    box-shadow: 0px 0px 4px 2px rgba(206, 206, 206, 0.5);
  }
  :deep(.el-input__inner) {
    font-size: 14px;
    font-family: Roboto;
  }
  :deep(.el-input) {
    .el-input__suffix {
      display: flex;
      align-items: center;
    }
    .el-input__prefix {
      display: flex;
      align-items: center;
    }
  }
  //表单
  :deep(.el-form-item) {
    margin-bottom: 20px;
  }
  :deep(.el-form-item__error) {
    padding-top: 4px;
  } //标签页
  :deep(.el-tabs__item.is-active) {
    color: $ringdata-color;
  }
  :deep(.el-tabs__item:hover) {
    color: $ringdata-color;
  }
  :deep(.el-tabs__active-bar) {
    background-color: $ringdata-color;
  }
}
</style>
